import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  Lightbulb,
  Search,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  entities as entitiesApi,
  signals as signalsApi,
  type ApiSignal,
} from "@/lib/api";
import { useTimeRange } from "@/lib/use-time-range";

export const Route = createFileRoute("/$workspace/signals")({
  head: () => ({ meta: [{ title: "Signals — Dravian Horizon" }] }),
  component: SignalsPage,
});

const TYPE_COLOR: Record<string, string> = {
  incident_report: "#e55a3c",
  news_article: "#3b82f6",
  displacement_event: "#f59e0b",
  military_communique: "#a855f7",
  prediction: "#a855f7",
  arrest: "#22b8d4",
  default: "#22c55e",
};
const colorForType = (t: string) => TYPE_COLOR[t] ?? TYPE_COLOR.default;

function SignalsPage() {
  const { workspace: id } = useParams({ from: "/$workspace/signals" });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const sigs = useQuery({
    queryKey: ["signals", id],
    queryFn: () => signalsApi.list(id),
  });
  const ents = useQuery({
    queryKey: ["entities", id],
    queryFn: () => entitiesApi.list(id),
  });

  const [type, setType] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const tr = useTimeRange();

  const deleteMut = useMutation({
    mutationFn: (signalId: string) => signalsApi.remove(id, signalId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["signals", id] }),
  });

  const all = sigs.data ?? [];

  // Derive available filter options from real data
  const typeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of all) counts.set(s.type, (counts.get(s.type) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [all]);

  const sourceOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of all) counts.set(s.source, (counts.get(s.source) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [all]);

  // Apply filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((s) => (type ? s.type === type : true))
      .filter((s) => (source ? s.source === source : true))
      .filter((s) => (tr.isActive ? tr.inRange(s.timestamp) : true))
      .filter((s) => {
        if (!q) return true;
        if (s.type.toLowerCase().includes(q)) return true;
        if (s.source.toLowerCase().includes(q)) return true;
        const attrs = s.attributes ?? {};
        for (const v of Object.values(attrs)) {
          if (typeof v === "string" && v.toLowerCase().includes(q)) return true;
          if (typeof v === "number" && String(v).includes(q)) return true;
        }
        return false;
      })
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [all, type, source, tr, search]);

  const entityNameById = useMemo(
    () => Object.fromEntries((ents.data ?? []).map((e) => [e.id, e.name])),
    [ents.data],
  );

  const openEntity = (entityId: string) =>
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, entity: entityId }),
    });

  return (
    <div className="p-5 max-w-[1200px] mx-auto space-y-4">
      <div>
        <div className="label-eyebrow mb-1">Signals</div>
        <h2 className="text-[20px] font-semibold">Signal feed</h2>
        <p className="text-[12px] text-muted-foreground mt-1">
          Every time-stamped event involving entities in this workspace — incidents, news, predictions, displacement events. Filter the noise, click an entity to inspect, delete what's not useful.
        </p>
      </div>

      <div className="panel p-3 space-y-2.5">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search across attributes (title, description, claim, source outlet…)"
            className="flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground/70"
          />
          <span className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
            {filtered.length} / {all.length}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-border">
          <FilterGroup
            label="Type"
            current={type}
            onChange={setType}
            options={typeOptions}
            colorize
          />
          <FilterGroup
            label="Source"
            current={source}
            onChange={setSource}
            options={sourceOptions}
          />
          {tr.isActive && (
            <span className="text-[11px] text-muted-foreground">
              Time window from topbar:{" "}
              <span className="font-mono text-foreground/80">
                {tr.from ?? "−∞"} → {tr.to ?? "now"}
              </span>
            </span>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="panel p-6 text-center text-[12px] text-muted-foreground">
          {all.length === 0
            ? "No signals yet. Use chat, RSS ingestion, or hypotheses generation to populate the feed."
            : "No signals match the current filters."}
        </div>
      ) : (
        <div className="panel divide-y divide-border overflow-hidden">
          {filtered.map((s) => (
            <SignalRow
              key={s.id}
              signal={s}
              expanded={expanded === s.id}
              onToggle={() => setExpanded((e) => (e === s.id ? null : s.id))}
              entityNameById={entityNameById}
              onOpenEntity={openEntity}
              onDelete={() => {
                if (confirm("Delete this signal? This cannot be undone.")) {
                  deleteMut.mutate(s.id);
                }
              }}
              deleting={deleteMut.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterGroup({
  label,
  current,
  onChange,
  options,
  colorize = false,
  formatLabel,
}: {
  label: string;
  current: string | null;
  onChange: (v: string | null) => void;
  options: [string, number][];
  colorize?: boolean;
  formatLabel?: (v: string) => string;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
        {label}
      </span>
      <button
        onClick={() => onChange(null)}
        className={`rounded px-2 py-0.5 text-[11px] transition ${
          current == null
            ? "bg-primary/20 text-primary"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        All
      </button>
      {options.map(([v, count]) => {
        const active = current === v;
        const dot = colorize ? colorForType(v) : null;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={`inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] transition ${
              active
                ? "bg-primary/20 text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {dot && (
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: dot }} />
            )}
            <span>{formatLabel ? formatLabel(v) : v}</span>
            {count > 0 && <span className="text-muted-foreground/70 font-mono">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function SignalRow({
  signal,
  expanded,
  onToggle,
  entityNameById,
  onOpenEntity,
  onDelete,
  deleting,
}: {
  signal: ApiSignal;
  expanded: boolean;
  onToggle: () => void;
  entityNameById: Record<string, string>;
  onOpenEntity: (entityId: string) => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const a = signal.attributes ?? {};
  const isPrediction = signal.type === "prediction";

  const summary =
    (a.claim as string) ||
    (a.title as string) ||
    (a.headline as string) ||
    (a.description as string) ||
    (a.summary as string) ||
    humanizeType(signal.type);

  const sourceOutlet = (a.source_outlet as string) || (a.article_title as string);

  return (
    <div className={`group ${isPrediction ? "border-l-2 border-l-[#a855f7]" : ""}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <button onClick={onToggle} className="mt-1 shrink-0 text-muted-foreground hover:text-foreground">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
        <span
          className="mt-1.5 h-2.5 w-2.5 rounded-full shrink-0"
          style={{
            background: colorForType(signal.type),
            boxShadow: `0 0 8px ${colorForType(signal.type)}`,
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-foreground/95 leading-snug">
            {isPrediction && (
              <span className="inline-flex items-center gap-1 rounded bg-[#a855f7]/15 text-[#a855f7] border border-[#a855f7]/30 px-1.5 py-0.5 text-[9px] font-semibold tracking-wider uppercase mr-2 align-middle">
                <Lightbulb className="h-2.5 w-2.5" />
                Hypothesis
              </span>
            )}
            {summary}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
            <span>{signal.type}</span>
            <span>·</span>
            <span>{signal.source}</span>
            {sourceOutlet && (
              <>
                <span>·</span>
                <span>{sourceOutlet}</span>
              </>
            )}
            {signal.related_entity_ids.length > 0 && (
              <>
                <span>·</span>
                <span className="flex flex-wrap gap-1">
                  {signal.related_entity_ids.slice(0, 4).map((eid) => (
                    <button
                      key={eid}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenEntity(eid);
                      }}
                      className="hover:text-primary"
                    >
                      {entityNameById[eid] ?? eid.slice(0, 8)}
                    </button>
                  ))}
                  {signal.related_entity_ids.length > 4 && (
                    <span>+{signal.related_entity_ids.length - 4}</span>
                  )}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          <span
            className="text-[11px] text-muted-foreground font-mono whitespace-nowrap mt-0.5"
            title={new Date(signal.timestamp).toLocaleString()}
          >
            {relativeTime(signal.timestamp)}
          </span>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="opacity-0 group-hover:opacity-100 transition rounded p-1 text-muted-foreground hover:text-risk-high hover:bg-risk-high/10 disabled:opacity-50"
            title="Delete signal"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pl-12 text-[12px] space-y-2">
          <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-1 text-[11px]">
            <div className="text-muted-foreground">Timestamp</div>
            <div className="font-mono text-foreground/90">
              {new Date(signal.timestamp).toLocaleString()}
            </div>
            <div className="text-muted-foreground">Confidence</div>
            <div className="text-foreground/90">{signal.confidence.toFixed(2)}</div>
            <div className="text-muted-foreground">Signal ID</div>
            <div className="font-mono text-foreground/70 text-[10px]">{signal.id}</div>
          </div>
          {Object.entries(a).length > 0 && (
            <div>
              <div className="label-eyebrow !mb-1">Attributes</div>
              <dl className="grid grid-cols-[140px_minmax(0,1fr)] gap-y-1 text-[11px]">
                {Object.entries(a).map(([k, v]) => (
                  <RowKV key={k} k={k} v={v} />
                ))}
              </dl>
            </div>
          )}
          {signal.related_entity_ids.length > 0 && (
            <div>
              <div className="label-eyebrow !mb-1">Related entities</div>
              <div className="flex flex-wrap gap-1.5">
                {signal.related_entity_ids.map((eid) => (
                  <button
                    key={eid}
                    onClick={() => onOpenEntity(eid)}
                    className="rounded border border-border bg-surface-elevated/60 px-2 py-0.5 text-[11px] hover:border-primary/40 hover:text-primary"
                  >
                    {entityNameById[eid] ?? eid.slice(0, 8)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RowKV({ k, v }: { k: string; v: unknown }) {
  let display: string;
  if (typeof v === "string") display = v;
  else if (typeof v === "number") display = String(v);
  else if (typeof v === "boolean") display = String(v);
  else if (v == null) display = "—";
  else display = JSON.stringify(v);
  return (
    <>
      <dt className="text-muted-foreground break-words">{k}</dt>
      <dd className="text-foreground/90 break-words whitespace-pre-wrap">{display}</dd>
    </>
  );
}

function humanizeType(t: string): string {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "in the future";
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}
